import { ComponentProps, useCallback, useMemo, useState } from 'react';

import { useListEvents } from '@givebacks/api/store';
import { useBreakPoints } from 'core';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ActionIcon,
  ButtonMantine,
  Center,
  EventCard,
  Loader,
  NoResults,
  SimpleGrid,
  Stack,
  TextInput,
  Title,
} from 'ui';
import { useDebouncedValue } from 'ui/Hooks';
import { IconSearch, IconX } from 'ui/Icons';

import { ViewAllEventsModal } from './ViewAllEventsModal/ViewAllEventsModal';

type EventsSectionProps = {
  causeId?: string;

  /**
   * The maximum number of events to display. Set to 'none' to display all events.
   */
  eventLimit?: number | 'none';

  /**
   * Props passed to the underlying EventCard component
   */
  eventCardProps?: ComponentProps<typeof EventCard>;

  /**
   * If true, the events will be stacked in a single column
   */
  isStacked?: boolean;

  /**
   * The title of the section. Set to empty string to hide the title.
   */
  title?: string;

  /**
   * Props passed to the underlying Stack component
   */
  containerProps?: ComponentProps<typeof Stack>;

  /**
   * If provided, will override the default navigation behavior
   */
  navigateToEvent?: (_navigate: (to: string) => void, eventId: string) => void;
};

const EventsSection = ({
  causeId,
  eventLimit = 3,
  eventCardProps,
  isStacked = false,
  title = 'Ticketed Events',
  containerProps,
  navigateToEvent,
}: EventsSectionProps) => {
  const navigate = useNavigate();
  const [isViewAllEventsModalOpen, setIsViewAllEventsModalOpen] = useState(false);
  const { isSmallScreen } = useBreakPoints();

  const [searchParams, setSearchParams] = useSearchParams();
  const searchQuery = searchParams.get('search') ?? '';
  const [debouncedSearchQuery] = useDebouncedValue(searchQuery, 500);

  const setSearchQuery = useCallback(
    (value: string) => {
      const searchParamsCopy = new URLSearchParams(searchParams);
      if (value) {
        searchParamsCopy.set('search', value);
      } else {
        searchParamsCopy.delete('search');
      }
      setSearchParams(searchParamsCopy);
    },
    [setSearchParams, searchParams]
  );

  const { data: events, isFetching } = useListEvents({
    causeId,
    queryOptions: {
      searches: {
        live: {
          value: true,
        },
        ...(debouncedSearchQuery && {
          name: {
            value: debouncedSearchQuery,
          },
        }),
      },
      options: {
        include: 'event_dates',
        total: true,
      },
    },
    pagination: {
      limit: eventLimit === 'none' ? undefined : eventLimit,
    },
  });

  const handleNavigateToEvent = useCallback(
    (eventId: string) => {
      if (navigateToEvent) {
        navigateToEvent(navigate, eventId);
      } else {
        navigate(`events/${eventId}`);
      }
    },
    [navigateToEvent, navigate]
  );

  const renderedEvents = useMemo(() => {
    return events?.store_items?.map((event) => (
      <EventCard
        key={event.uuid}
        imageSrc={event.images?.[0]?.url}
        thumbnailSrc={event.thumbnail?.url}
        name={event.name}
        onClick={() => handleNavigateToEvent(event.uuid)}
        start_date={event.earliest_starting_at}
        end_date={event.latest_ending_at}
        isColumn={!isSmallScreen}
        {...eventCardProps}
      />
    ));
  }, [events?.store_items, isSmallScreen, handleNavigateToEvent, eventCardProps]);

  const hasResults = useMemo(() => {
    return events?.store_items && events.store_items.length > 0;
  }, [events?.store_items]);

  return (
    <>
      <Stack gap="md" {...containerProps}>
        {title && (
          <Title order={2} c="gray.8">
            {title}
          </Title>
        )}

        <TextInput
          placeholder="Search Events"
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.currentTarget.value)}
          leftSection={<IconSearch size={16} />}
          rightSection={
            searchQuery ? (
              <ActionIcon
                size="sm"
                variant="transparent"
                c="gray"
                aria-label="Clear search"
                onClick={() => setSearchQuery('')}
              >
                <IconX size={16} brand="tabler" />
              </ActionIcon>
            ) : null
          }
          mb="sm"
        />

        {isFetching && (
          <Center>
            <Loader />
          </Center>
        )}

        {!isFetching &&
          (hasResults ? (
            <SimpleGrid
              cols={{ base: 1, sm: isStacked ? 1 : 3 }}
              spacing={{ base: 'sm', md: 'md' }}
              mb="xs"
            >
              {renderedEvents}
            </SimpleGrid>
          ) : (
            <NoResults isLoading={false} />
          ))}

        {events?.meta?.has_more && (
          <ButtonMantine
            w={122}
            size="sm"
            mx="auto"
            display="block"
            onClick={() => setIsViewAllEventsModalOpen(true)}
          >
            View All
          </ButtonMantine>
        )}
      </Stack>

      <ViewAllEventsModal
        opened={isViewAllEventsModalOpen}
        onClose={() => setIsViewAllEventsModalOpen(false)}
        causeId={causeId}
      />
    </>
  );
};

export { EventsSection };
